import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLazyGetContaTotemCheckoutQuery } from '@/api'
import { atualizarKiosk } from '@/store/self-service-kiosk-slice'
import type { RootState } from '@/store'

const TOLERANCIA_CENTAVO = 0.005

export interface ReconferenciaConta {
    verificando: boolean
    erro: boolean
    semVinculo: boolean
    totalAnterior: number | null
    pedidoIndisponivel: boolean
    confirmarCiencia: () => void
    tentarNovamente: () => void
    reconferir: () => void
}

export function useReconferirConta(): ReconferenciaConta {
    const dispatch = useDispatch()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const [buscarConta] = useLazyGetContaTotemCheckoutQuery()
    const [verificando, setVerificando] = useState(true)
    const [erro, setErro] = useState(false)
    const [totalAnterior, setTotalAnterior] = useState<number | null>(null)
    const [pedidoIndisponivel, setPedidoIndisponivel] = useState(false)
    const [tentativa, setTentativa] = useState(0)

    const baseRef = useRef({
        idPedido: order?.idPedidoIntegracao ?? null,
        total: order?.total ?? 0,
        identificador: identificadorBuscado,
    })

    useEffect(() => {
        const { idPedido, total: totalNaTela, identificador } = baseRef.current

        if (!idPedido || !identificador) {
            setVerificando(false)
            return
        }

        let cancelado = false

        void (async () => {
            try {
                const conta = await buscarConta({ identificador }).unwrap()
                if (cancelado) return

                const atualizado = (conta.pedidos ?? []).find((p) => p.idPedidoIntegracao === idPedido)

                if (!atualizado || atualizado.bloqueadoNoTotem) {
                    setPedidoIndisponivel(true)
                    return
                }

                dispatch(atualizarKiosk({ order: atualizado, multipleOrders: conta.pedidos ?? [] }))

                if (Math.abs((atualizado.total ?? 0) - totalNaTela) > TOLERANCIA_CENTAVO) {
                    setTotalAnterior(totalNaTela)
                }
            } catch (e) {
                console.error('Falha ao reconferir a conta antes do pagamento:', e)
                if (!cancelado) setErro(true)
            } finally {
                if (!cancelado) setVerificando(false)
            }
        })()

        return () => {
            cancelado = true
        }
    }, [tentativa, buscarConta, dispatch])

    const confirmarCiencia = useCallback(() => setTotalAnterior(null), [])

    const tentarNovamente = useCallback(() => {
        setErro(false)
        setVerificando(true)
        setTentativa((t) => t + 1)
    }, [])

    const semVinculo = !baseRef.current.idPedido || !baseRef.current.identificador

    return { verificando, erro, semVinculo, totalAnterior, pedidoIndisponivel, confirmarCiencia, tentarNovamente, reconferir: tentarNovamente }
}
